export class CalendarList {
  /**
   * Group of calendar items for a month
   *
   * @protected
   */
  protected calendarItemGroups?: NodeListOf<HTMLElement>;
  /**
   * The calendar list items to filter
   *
   * @protected
   */
  protected calendarItems?: NodeListOf<HTMLElement>;

  /**
   * The type filters
   *
   * @protected
   */
  protected typeFilters?: NodeListOf<HTMLInputElement>;

  /**
   * Active type filters array
   *
   * @protected
   */
  protected activeTypeFilters?: Array<string> = [];

  /**
   * The calendar wrapper class
   *
   * @protected
   */
  protected el: HTMLElement | null;

  /**
   * The calendar item list wrapper class
   *
   * @protected
   */
  protected itemsWrapper: HTMLElement | null;

  /**
   * The type filter reset button
   *
   * @protected
   */
  protected typeFilterReset?: HTMLElement | null;

  /**
   * The today filter button
   *
   * @protected
   */
  protected todayFilter?: HTMLElement | null;

  /**
   * The month filter select
   *
   * @protected
   */
  protected monthFilter?: HTMLSelectElement | null;

  /**
   * The year filter select
   *
   * @protected
   */
  protected yearFilter?: HTMLSelectElement | null;

  /**
   * Button to click to prev month
   *
   * @protected
   */
  protected prevMonth?: HTMLElement | null;

  /**
   * Button to click to next month
   *
   * @protected
   */
  protected nextMonth?: HTMLElement | null;

  /**
   * The information about no events were found
   *
   * @protected
   */
  protected noResultsFound?: HTMLElement | null;



  /**
   * Calendar options
   *
   * @protected
   */
  protected options: CalendarOptions;

  /**
   * Current month
   *
   * @protected
   */
  protected currentMonth: number;

  /**
   * Current year
   *
   * @protected
   */
  protected currentYear: number;

  constructor(options = {}) {
    const defaults: CalendarOptions = {
      wrapperClass: 'js-vrs-calendar-wrapper',
      itemsWrapperClass: 'js-vrscalendar-list-wrapper',
      calendarItemGroupsClass: 'js-vrscalendar-list-group',
      calendarItemGroupHideClass: 'js-vrscalendar-list-group--hide',
      calendarItemClass: 'js-vrscalendar-list__item',
      calendarItemActiveClass: 'js-vrscalendar-list__item--active',
      calendarItemActiveClass2: 'vrscalendar-list__item--active',
      typeFilterClass: 'js-vrscalendar-filter-type',
      typeFilterResetClass: 'js-vrscalendar-filter-type-reset',
      typeFilterResetActiveClass: 'js-vrscalendar-filter-type-reset--active',
      todayFilterClass: 'js-vrscalendar-filter-today',
      monthFilterSelectClass: 'js-vrscalendar-filter-month',
      yearFilterSelectClass: 'js-vrscalendar-filter-year',
      noResultsFoundClass: 'js-vrscalendar-list-no-results',
      noResultsFoundActiveClass: 'js-vrscalendar-list-no-results--active',
      prevMonthClass: 'js-vrscalendar-prev-month',
      nextMonthClass: 'js-vrscalendar-next-month',

      setCurrentMonthActive: true,
    };

    this.options = {
      ...defaults,
      ...options,
    };

    this.el = document.querySelector(`.${this.options.wrapperClass}`);
    this.itemsWrapper = document.querySelector(`.${this.options.itemsWrapperClass}`);
    // get current month (+1 due to index of first month is 0)
    this.currentMonth = new Date().getMonth() + 1;
    this.currentYear = new Date().getFullYear();

    if (this.el !== null) {
      this.initialize();
    }
  }

  protected initialize(): void {
    this.calendarItemGroups = this.el?.querySelectorAll(`.${this.options.calendarItemGroupsClass}`) as NodeListOf<HTMLElement>;
    this.calendarItems = this.el?.querySelectorAll(`.${this.options.calendarItemClass}`) as NodeListOf<HTMLElement>;
    this.typeFilters = this.el?.querySelectorAll(`.${this.options.typeFilterClass}`) as NodeListOf<HTMLInputElement>;
    this.typeFilterReset = this.el?.querySelector(`.${this.options.typeFilterResetClass}`);
    this.todayFilter = this.el?.querySelector(`.${this.options.todayFilterClass}`);
    this.monthFilter = this.el?.querySelector(`.${this.options.monthFilterSelectClass}`);
    this.yearFilter = this.el?.querySelector(`.${this.options.yearFilterSelectClass}`);
    this.noResultsFound = this.el?.querySelector(`.${this.options.noResultsFoundClass}`);
    this.prevMonth = this.el?.querySelector(`.${this.options.prevMonthClass}`);
    this.nextMonth = this.el?.querySelector(`.${this.options.nextMonthClass}`);

    if (this.calendarItems !== null && this.typeFilters !== null) {
      this.initTypeFilters();
    }

    if (this.typeFilterReset !== null) {
      this.initTypeFilterReset();
    }

    if (this.todayFilter !== null) {
      this.initTodayFilter();
    }

    if (this.prevMonth !== null) {
      this.changeToPrevMonth();
    }

    if (this.monthFilter !== null) {
      this.initMonthFilter();
    }

    if (this.nextMonth !== null) {
      this.changeToNextMonth();
    }

    if (this.yearFilter !== null) {
      this.initYearFilter();
    }

    if (this.options.setCurrentMonthActive) {
      this.todayFilter?.click();
    }
  }

  protected initTypeFilters(): void {
    this.typeFilters?.forEach((typeFilter) => {
      typeFilter.addEventListener('change', (event) => {
        this.handleEvent(event, typeFilter);
      });
      typeFilter.addEventListener('keyup', (event) => {
        this.handleEvent(event, typeFilter);
      });
    });
  }

  protected handleEvent(event, typeFilter): void {
    // Überprüfen, ob das Event ein 'change' Event ist
    if (event.type === 'change') {
      this.onChangeTypeFilter();
      this.checkIfAnyEventIsActive();
    }

    // Überprüfen, ob das Event ein 'keyup' Event ist und ob die gedrückte Taste Enter oder Leertaste ist
    if (event.type === 'keyup') {
      if (event.key === 'Enter' || event.key === ' ') {
        if (typeFilter.checked) {
            typeFilter.checked = false;
        } else {
            typeFilter.checked = true;
        }
        this.onChangeTypeFilter();
        this.checkIfAnyEventIsActive();
      }
    }
  }

  protected initTodayFilter(): void {
    this.todayFilter?.addEventListener('click', () => {

      // set year filter to current year
      if (this.yearFilter) {
        this.yearFilter.value = this.currentYear.toString();
      }
      // set month filter to current month
      if (this.monthFilter) {
        this.monthFilter.selectedIndex = this.currentMonth;
      }
      // trigger filter handling for month
      this.setMonthFilterGroups();

      this.checkIfAnyEventIsActive();
    });
  }

  protected initMonthFilter(): void {
    this.monthFilter?.addEventListener('change', () => {
      // default value "all months"
      if (this.monthFilter?.value === '0') {
        // Disable next and prev button for month
        this.prevMonth?.setAttribute('disabled', 'true');
        this.nextMonth?.setAttribute('disabled', 'true');

        this.prevMonth?.classList.add('disabled');
        this.nextMonth?.classList.add('disabled');

        if (this.yearFilter) {
          this.yearFilter.value = '0';
        }
        this.toggleCalendarItemGroups();
      } else {
        // Enable next and prev button for month
        this.prevMonth?.removeAttribute('disabled');
        this.nextMonth?.removeAttribute('disabled');

        this.prevMonth?.classList.remove('disabled');
        this.nextMonth?.classList.remove('disabled');

        if (
          this.monthFilter &&
          this.yearFilter &&
          this.prevMonth &&
          (parseInt(this.monthFilter.value, 10) === 1 || parseInt(this.monthFilter.value, 10) === 12)
        ) {
          let lastOptionYear = 1970;
          let firstOptionYear = 1970;
          const lastOptionsIndex = (this.yearFilter?.options.length - 1);
          Array.prototype.forEach.call(this.yearFilter?.options, function (option, index) {
            if (firstOptionYear === 1970 && parseInt(option.value, 10) !== 0) {
              firstOptionYear = option.value;
            }
            if (index === lastOptionsIndex) {
              lastOptionYear = option.value;
            }
          });
          if (
            (this.yearFilter.value === firstOptionYear.toString() || parseInt(this.yearFilter.value, 10) === 0) &&
            parseInt(this.monthFilter.value, 10) === 1
          ) {
              this.prevMonth?.setAttribute('disabled', 'true');
          } else if (
                      (this.yearFilter.value === lastOptionYear.toString() || parseInt(this.yearFilter.value, 10) === 0) &&
                      parseInt(this.monthFilter.value, 10) === 12
            ) {
            this.nextMonth?.setAttribute('disabled', 'true');
          } else {
            // Enable next and prev button for month
            this.prevMonth?.removeAttribute('disabled');
            this.nextMonth?.removeAttribute('disabled');
          }
        }  else {
          // Enable next and prev button for month
          this.prevMonth?.removeAttribute('disabled');
          this.nextMonth?.removeAttribute('disabled');
        }
        this.setMonthFilterGroups();
      }
      this.checkIfAnyEventIsActive();
    });
  }

  protected setMonthFilterGroups(): void {
    this.calendarItemGroups?.forEach((group: HTMLElement) => {
      let condition: boolean;
      if (this.yearFilter?.value !== '0') {
        condition = this.monthFilter?.value === group.dataset.vrscalendarMonth && this.yearFilter?.value === group.dataset.vrscalendarYear;
      } else {
        condition = this.monthFilter?.value === group.dataset.vrscalendarMonth;
      }
      if (condition) {
        // check if selected month has active items
        const activeItemsInSelectedGroup = group.getElementsByClassName(<string>this.options.calendarItemActiveClass);

        // hide group if group has no active items
        if (activeItemsInSelectedGroup.length > 0) {
          group.classList.remove(<string>this.options.calendarItemGroupHideClass);
        } else {
          group.classList.add(<string>this.options.calendarItemGroupHideClass);
        }
      } else {
        group.classList.add(<string>this.options.calendarItemGroupHideClass);
      }
    });
  }

  protected initYearFilter(): void {
    this.yearFilter?.addEventListener('change', (event) => {
      event.stopPropagation();
      // default value "all years"
      if (this.yearFilter?.value === '0') {
        if (this.monthFilter) {
          this.monthFilter.value = '0';
          // Disable next and prev button for month
          this.prevMonth?.setAttribute('disabled', 'true');
          this.nextMonth?.setAttribute('disabled', 'true');

          this.prevMonth?.classList.add('disabled');
          this.nextMonth?.classList.add('disabled');
        }
        this.toggleCalendarItemGroups();
      } else {
        this.calendarItemGroups?.forEach((group: HTMLElement) => {
          let condition: boolean = false;
          if (this.monthFilter?.value !== '0') {
            this.prevMonth?.classList.remove('disabled');
            this.nextMonth?.classList.remove('disabled');
            if (this.yearFilter?.value === group.dataset.vrscalendarYear && this.monthFilter?.value === group.dataset.vrscalendarMonth) {
              condition = true;
            }

            if (
              this.monthFilter &&
              this.yearFilter &&
              this.prevMonth &&
              (parseInt(this.monthFilter.value, 10) === 1 || parseInt(this.monthFilter.value, 10) === 12)
            ) {
              let lastOptionYear = 1970;
              let firstOptionYear = 1970;
              const lastOptionsIndex = (this.yearFilter?.options.length - 1);
              Array.prototype.forEach.call(this.yearFilter?.options, function (option, index) {
                if (firstOptionYear === 1970 && parseInt(option.value, 10) !== 0) {
                  firstOptionYear = option.value;
                }
                if (index === lastOptionsIndex) {
                  lastOptionYear = option.value;
                }
              });
              if (this.yearFilter.value === firstOptionYear.toString() && parseInt(this.monthFilter.value, 10) === 1) {
                  this.prevMonth?.setAttribute('disabled', 'true');
              } else if (this.yearFilter.value === lastOptionYear.toString() && parseInt(this.monthFilter.value, 10) === 12) {
                this.nextMonth?.setAttribute('disabled', 'true');
              } else {
                // Enable next and prev button for month
                this.prevMonth?.removeAttribute('disabled');
                this.nextMonth?.removeAttribute('disabled');
              }
            }  else {
              // Enable next and prev button for month
              this.prevMonth?.removeAttribute('disabled');
              this.nextMonth?.removeAttribute('disabled');

              this.prevMonth?.classList.add('disabled');
              this.nextMonth?.classList.add('disabled');
            }
          } else {
            this.prevMonth?.setAttribute('disabled', 'true');
            this.nextMonth?.setAttribute('disabled', 'true');

            if (this.yearFilter?.value === group.dataset.vrscalendarYear) {
              condition = true;
            }
          }
          if (condition) {
            group.classList.remove(<string>this.options.calendarItemGroupHideClass);
          } else {
            group.classList.add(<string>this.options.calendarItemGroupHideClass);
            const groupItems = group.querySelectorAll('.' + <string>this.options.calendarItemClass);
            if (groupItems.length > 0) {
              groupItems.forEach((item: HTMLElement) => {
                item.classList.add('ignore');
              });
            }
          }
        });
      }

      this.toggleCalendarItemGroups();
      this.checkIfAnyEventIsActive();
    });
  }

  protected initTypeFilterReset(): void {
    this.typeFilterReset?.addEventListener('click', () => {
      this.typeFilterReset?.classList.add(<string>this.options.typeFilterResetActiveClass);
      this.calendarItems?.forEach((item: HTMLElement) => item.classList.add(<string>this.options.calendarItemActiveClass));
      this.calendarItems?.forEach((item: HTMLElement) => item.classList.add(<string>this.options.calendarItemActiveClass2));

      this.typeFilters?.forEach((filter: HTMLInputElement) => {
        filter.checked = false;
        const ariaLabelContent = filter.dataset.default;
        if (ariaLabelContent) {
          filter.setAttribute('aria-label', ariaLabelContent);
        }
      });

      this.calendarItemGroups?.forEach((group: HTMLElement) => group.classList.remove(<string>this.options.calendarItemGroupHideClass));
      // reset active filters array
      this.activeTypeFilters = [];

      this.resetMonthYearFilter(true);

      this.checkIfAnyEventIsActive();
    });
  }

  protected onChangeTypeFilter(): void {
    this.activeTypeFilters = [];
    this.typeFilterReset?.classList.remove(<string>this.options.typeFilterResetActiveClass);
    this.typeFilters?.forEach((filter) => {
      let ariaLabelContent = filter.dataset.default;

      // Change aria-label attribute of checkbox
      if (filter.checked === true) {
        ariaLabelContent = filter.dataset.active;

        // add filter option to active filters array
        if (!this.activeTypeFilters?.includes(filter.value)) {
          this.activeTypeFilters?.push(filter.value);
        }
      }

      if (ariaLabelContent) {
        filter.setAttribute('aria-label', ariaLabelContent);
      }

      this.filterCalendarItems(filter.value, filter.checked);
    });

    // if no more filter is active, reset selection to show all
    if (this.activeTypeFilters.length === 0) {
      this.typeFilterReset?.click();
      this.todayFilter?.click();
    } else {
      this.resetMonthYearFilter(true);
    }
  }

  protected resetMonthYearFilter(showCurrentYear: boolean = false): void {
    if (this.monthFilter) {
      this.monthFilter.value = '0';
    }
    if (this.yearFilter) {
      if (showCurrentYear) {
        this.calendarItemGroups?.forEach((group: HTMLElement) => {
          if (group.dataset.vrscalendarYear === this.yearFilter?.value || this.yearFilter?.value === '0') {
            group.classList.remove(<string>this.options.calendarItemGroupHideClass);
          } else {
            group.classList.add(<string>this.options.calendarItemGroupHideClass);
            const groupItems = group.querySelectorAll('.' + <string>this.options.calendarItemClass);
            if (groupItems.length > 0) {
              groupItems.forEach((item: HTMLElement) => {
                item.classList.add('ignore');
              });
            }
          }
        });

        // toggle visibility of calendar groups which have no visible calendar items
        this.toggleCalendarItemGroups();

      } else {
        this.yearFilter.value = '0';
      }
    }

  }

  protected filterCalendarItems(typeFilter: string, filterChecked: boolean): void {
    // show calendar items which are matching filter type
    this.calendarItems?.forEach((item) => {
      if (item.dataset.vrscalendarType === typeFilter) {
        this.handleCalendarItemVisibility(filterChecked, item);
      }
    });

    // toggle visibility of calendar groups which have no visible calendar items
    this.toggleCalendarItemGroups();
  }

  protected handleCalendarItemVisibility(isItemValid: boolean, calendarItem: HTMLElement): void {
    if (isItemValid) {
      calendarItem.classList.add(<string>this.options.calendarItemActiveClass);
      calendarItem.classList.add(<string>this.options.calendarItemActiveClass2);
    } else {
      calendarItem.classList.remove(<string>this.options.calendarItemActiveClass);
      calendarItem.classList.remove(<string>this.options.calendarItemActiveClass2);
    }
  }

  protected toggleCalendarItemGroups(): void {
    this.calendarItemGroups?.forEach((group) => {
      let hasActiveItem = false;
      group.querySelectorAll(`.${this.options.calendarItemClass}:not(.ignore)`).forEach((item: HTMLElement) => {
        if (item.classList.contains(<string>this.options.calendarItemActiveClass)) {
          hasActiveItem = true;
          return;
        }
      });

      if (!hasActiveItem) {
        group.classList.add(<string>this.options.calendarItemGroupHideClass);
      } else {
        group.classList.remove(<string>this.options.calendarItemGroupHideClass);
      }
    });

    const editedItems = document.querySelectorAll('.ignore');
    if (editedItems) {
      editedItems.forEach((item: HTMLElement) => {
        item.classList.remove('ignore');
      });
    }
  }

  protected checkIfAnyEventIsActive(): void {
    // get all month groups
    const childItemsTotal = <HTMLCollection>this.itemsWrapper?.children;
    // get all month groups which are hidden
    const childItemsHidden = this.itemsWrapper?.getElementsByClassName(<string>this.options.calendarItemGroupHideClass);

    //console.log(childItemsTotal?.length + '' + childItemsHidden?.length);

    if (childItemsHidden?.length === childItemsTotal.length) {
      //console.log('Add class for not found');
      this.noResultsFound?.classList.add(<string>this.options.noResultsFoundActiveClass);
    } else {
      this.noResultsFound?.classList.remove(<string>this.options.noResultsFoundActiveClass);
    }
  }

  protected changeToPrevMonth(): void {
      this.prevMonth?.addEventListener('click', () => {
        let changeBool = false;

        // set month filter to current month
        if (this.monthFilter) {
          const selectedMonth = this.monthFilter.selectedIndex;
          if (selectedMonth !== 1) {
            this.monthFilter.selectedIndex = (selectedMonth - 1);
            changeBool = true;

            // Disable prev button if first entry
            if (selectedMonth === 2 && this.yearFilter) {
              let firstOptionYear = 1970;
              Array.prototype.forEach.call(this.yearFilter.options, function (option, index) {
                if (firstOptionYear === 1970 && parseInt(option.value, 10) !== 0) {
                  firstOptionYear = option.value;
                }
              });

              if (this.yearFilter.value === firstOptionYear.toString() || parseInt(this.yearFilter.value, 10) === 0) {
                  this.prevMonth?.setAttribute('disabled', 'true');
              }
            }
          } else {
            if (this.yearFilter) {

              const selectedYear = parseInt(this.yearFilter.value, 10);
              // Check if prev year exists
              let checkYearBool = false;
              const searchYear = (selectedYear - 1);
              const selectedMonthNew = 12;

              // this.yearFilter.value = this.currentYear.toString();
              Array.prototype.forEach.call(this.yearFilter.options, function (option, index) {
                if (searchYear ===  parseInt(option.value, 10)) {
                    checkYearBool = true;
                }
              });

              if (checkYearBool) {
                this.monthFilter.selectedIndex = selectedMonthNew;
                this.yearFilter.value = searchYear.toString();
                changeBool = true;
              }
            }
          }
          // if (this.monthFilter?.value === '0') {}
        }

        if (changeBool) {
          // trigger filter handling for month
          this.setMonthFilterGroups();
          this.nextMonth?.removeAttribute('disabled');
          this.checkIfAnyEventIsActive();
        }
      });
  }

  protected changeToNextMonth(): void {
      this.nextMonth?.addEventListener('click', () => {
        let changeBool = false;

        // set month filter to current month
        if (this.monthFilter) {
          const selectedMonth = this.monthFilter.selectedIndex;

          if (selectedMonth !== 12) {
            this.monthFilter.selectedIndex = (selectedMonth + 1);
            changeBool = true;

            // Disable next button if last entry
            if (selectedMonth === 11 && this.yearFilter) {
              let lastOptionYear = 1970;
              const lastOptionsIndex = (this.yearFilter.options.length - 1);
              Array.prototype.forEach.call(this.yearFilter.options, function (option, index) {
                if (index === lastOptionsIndex) {
                  lastOptionYear = option.value;
                }
              });
              if (this.yearFilter.value === lastOptionYear.toString() || parseInt(this.yearFilter.value, 10) === 0) {
                  this.nextMonth?.setAttribute('disabled', 'true');
              }
            }
          } else {
            if (this.yearFilter) {
              const selectedYear = parseInt(this.yearFilter.value, 10);
              // Check if prev year exists
              let checkYearBool = false;
              const searchYear = (selectedYear + 1);
              const selectedMonthNew = 1;

              Array.prototype.forEach.call(this.yearFilter.options, function (option, index) {
                if (searchYear ===  parseInt(option.value, 10)) {
                    checkYearBool = true;
                }
              });

              if (checkYearBool) {
                this.monthFilter.selectedIndex = selectedMonthNew;
                this.yearFilter.value = searchYear.toString();
                changeBool = true;
              }
            }
          }
        }

        if (changeBool) {
          this.prevMonth?.removeAttribute('disabled');
          // trigger filter handling for month
          this.setMonthFilterGroups();
          this.checkIfAnyEventIsActive();
        }
      });
  }
}

export interface CalendarOptions {
  wrapperClass?: string;
  itemsWrapperClass?: string;
  calendarItemGroupsClass?: string;
  calendarItemGroupHideClass?: string;
  calendarItemClass?: string;
  calendarItemActiveClass?: string;
  calendarItemActiveClass2?: string;
  typeFilterClass?: string;
  typeFilterResetClass?: string;
  typeFilterResetActiveClass?: string;
  todayFilterClass?: string;
  monthFilterSelectClass?: string;
  yearFilterSelectClass?: string;
  noResultsFoundClass?: string;
  noResultsFoundActiveClass?: string;
  prevMonthClass?: string;
  nextMonthClass?: string;
  setCurrentMonthActive: boolean;
}
